import { useState } from 'react';
import axios from 'axios';
import { Activity, Loader2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDataProvenanceHealthButton — Round 95y (Jun 2026).
 *
 * One-click admin observability tile that surfaces every ticker
 * currently being supplemented from the local 24-month dividend
 * cache instead of Massive's authoritative index. Useful before
 * a redeploy or when validating a freshly-added IPO — answers
 * "how much of my Stock Modal data depends on Massive catching up?"
 *
 * Hits `/api/admin/data-provenance-health` (admin-gated). The endpoint
 * returns three buckets:
 *   • growth_supplemented — tickers whose Div Growth tab + Streak Risk
 *     Chart are filled in from the 24m cache
 *   • events_supplemented — tickers whose Recent Events card is filled
 *     in from the 24m cache
 *   • union — unique tickers currently in any supplemented mode
 *
 * Counts of 0 across the board = Massive is fully indexing every
 * ticker you track; no fallback in play. The badge wedge surfaces
 * the union count so the admin sees the most important number first.
 */
export default function AdminDataProvenanceHealthButton({ onStockClick }) {
  // Round P111u — self-gate; hides even if wrapper is bypassed.
  const { user: _authUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const openModal = async () => {
    setOpen(true);
    setError(null);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/data-provenance-health`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Health check failed');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setData(null);
    setError(null);
  };

  const handleTickerClick = (ticker) => {
    if (onStockClick) {
      close();
      onStockClick(ticker);
    }
  };

  // Round P111u — self-gate (post-hooks; safe with Rules of Hooks).
  if (_authUser?.role !== 'admin') return null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="admin-data-provenance-health-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-900 dark:text-cyan-200 border border-cyan-500/40 transition-colors"
        title="Show tickers whose Stock Modal data is currently being supplemented from the 24-month local cache (Massive's index is still catching up)"
      >
        <Activity className="w-4 h-4" />
        Backfilled tickers
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={close}
          data-testid="admin-data-provenance-health-modal"
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-card border border-cyan-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-card border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Tickers in 24m backfill mode</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  These tickers&apos; Div Growth / Streak Risk / Recent Events
                  are supplemented from the local 24-month cache because
                  Massive&apos;s index hasn&apos;t fully caught up yet. They self-heal
                  on Massive&apos;s next index update.
                </p>
              </div>
              <button onClick={close} aria-label="Close" data-testid="admin-data-provenance-health-close" className="p-1 rounded hover:bg-muted/40">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="admin-data-provenance-health-loading">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning caches…
                </div>
              )}

              {error && (
                <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200" data-testid="admin-data-provenance-health-error">{error}</div>
              )}

              {data && !loading && (
                <>
                  <div className="grid grid-cols-3 gap-2" data-testid="admin-data-provenance-health-stats">
                    <Stat label="Div Growth backfilled" value={data.growth_supplemented_count} />
                    <Stat label="Recent Events backfilled" value={data.events_supplemented_count} />
                    <Stat label="Unique tickers" value={data.union_count} highlight />
                  </div>

                  {data.union_count === 0 ? (
                    <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200 text-center">
                      ✅ Massive is fully indexing every ticker. No 24m cache fallback in use right now.
                    </div>
                  ) : (
                    <div className="border border-border/60 rounded-md overflow-hidden">
                      <div className="px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-between">
                        <span>Affected tickers (newest first by alphabet)</span>
                        {data.truncated && <span className="text-amber-300">+{data.union_count - 50} more</span>}
                      </div>
                      <div className="max-h-72 overflow-y-auto p-2 grid grid-cols-3 sm:grid-cols-4 gap-1">
                        {data.union.map((tkr) => {
                          const inBoth = data.growth_supplemented.includes(tkr) && data.events_supplemented.includes(tkr);
                          const inGrowthOnly = !inBoth && data.growth_supplemented.includes(tkr);
                          const inEventsOnly = !inBoth && data.events_supplemented.includes(tkr);
                          const tag = inBoth ? 'both' : inGrowthOnly ? 'growth' : inEventsOnly ? 'events' : '';
                          const tagColor = inBoth
                            ? 'text-amber-300'
                            : inGrowthOnly ? 'text-emerald-300' : 'text-fuchsia-300';
                          return (
                            <button
                              key={tkr}
                              onClick={() => handleTickerClick(tkr)}
                              data-testid={`admin-data-provenance-row-${tkr}`}
                              className="flex items-center justify-between gap-1 px-2 py-1.5 rounded text-xs font-mono hover:bg-muted/30 transition-colors text-left"
                            >
                              <span className="font-bold text-cyan-200">{tkr}</span>
                              <span className={`text-[9px] uppercase tracking-wider ${tagColor}`}>{tag}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <p className="text-[10px] text-muted-foreground/70">
                    <span className="text-emerald-300">growth</span> = Div Growth tab supplemented · <span className="text-fuchsia-300">events</span> = Recent Events card supplemented · <span className="text-amber-300">both</span> = both
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div
      className={`rounded-md border p-2 text-center ${
        highlight
          ? 'border-cyan-500/40 bg-cyan-500/10'
          : 'border-border/60 bg-muted/20'
      }`}
      data-testid={`admin-data-provenance-stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground leading-tight">{label}</p>
      <p className={`text-2xl font-bold font-mono mt-1 ${highlight ? 'text-cyan-200' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}
